import { ReactElement, useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import OrgBox from './OrgBox'
import { I18n, Translate2 as Translate } from 'common'
import { Select, TextInput } from '../../../ui-components'
import { SalesPadOrg } from '../../../services/SalesPadServiceProvider'
import Header from './Header'
import { IdValueGeneric } from 'interfaces'
import { breakpoints } from '../../../theme/breakpoints'
import { Search } from '@mui/icons-material'
import { defaultTheme } from '../../../theme'

interface Props {
  orgs: SalesPadOrg[]
}

type Option = IdValueGeneric<string | number, string>

const Options: Option[] = [
  { id: 'all', value: 'All' },
  { id: 'csc only', value: 'CSC Only' },
]

const OrgGrid = (props: Props): ReactElement => {
  const { orgs } = props
  const [term, setTerm] = useState('')
  const [selectedOption, setSelectedOption] = useState<Option>(Options[0])
  const [cscOnly, setCSCOnly] = useState(false)

  const handleOnChange = (selected: Option) => {
    setSelectedOption(selected)
    setCSCOnly(selected.id?.toString() === Options[1].id.toString())
  }

  const filteredOrgs = useMemo<SalesPadOrg[]>(() => {
    if (!term && selectedOption.id === 'all') return orgs

    const matchesTerm = (org: SalesPadOrg) =>
      org.childOrgName?.toLowerCase().includes(term?.toLowerCase())

    const matchesCsc = (org: SalesPadOrg) =>
      selectedOption.id === 'all' || org.isCSC === cscOnly

    return orgs?.filter((org) => matchesTerm(org) && matchesCsc(org))
  }, [term, cscOnly, selectedOption.id, orgs])

  const onSearch = useCallback((newTerm: string, cscOnly: boolean): void => {
    setTerm(newTerm)
    setCSCOnly(cscOnly)
  }, [])

  return (
    <Wrapper>
      <Header />
      <FilterSection>
        <SearchBox>
          <TextInput
            placeholder={Translate('searchSalesPadUsersIndications')}
            onChange={(v) => onSearch(v, cscOnly)}
            type="text"
            value={term}
            width="100%"
            style={{
              background: 'inherit',
              border: '1px solid black',
              paddingRight: '34px',
            }}
            noMargin
            autoFocus
          />
          <StyledSearch />
        </SearchBox>
        <CSCOnlyFilter>
          <I18n name="filter" />
          <Select
            options={Options}
            selectedOption={selectedOption}
            onSelect={handleOnChange}
          />
        </CSCOnlyFilter>
      </FilterSection>
      <OrgBoxes>
        {filteredOrgs.map((o) => (
          <OrgBox key={o.childOrgId} org={o} />
        ))}
      </OrgBoxes>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;
  gap: 30px;
  max-width: 1920px;
  width: 100%;
  height: 100vh;
  background: transparent;
  padding: 24px;
`

const OrgBoxes = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-auto-rows: 100px;
  grid-gap: 24px;
  width: 100%;

  @media screen and (${breakpoints.tablet}px <= width < ${breakpoints.laptopMedium}px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media screen and (${breakpoints.mobileMedium}px <= width < ${breakpoints.tablet}px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (width < ${breakpoints.mobileMedium}px) {
    grid-template-columns: repeat(1, 1fr);
  }
`

const FilterSection = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 15px;
  width: 100%;
  height: auto;
  background-color: ${defaultTheme.colors.white};
  padding: 16px;
  border-top: 4px solid black;

  @media screen and (width < ${breakpoints.tablet}px) {
    flex-direction: column;
  }
`

const SearchBox = styled.div`
  display: flex;
  flex-grow: 1;
`

const CSCOnlyFilter = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: auto 0px;
  max-width: 245px;

  @media screen and (width < ${breakpoints.tablet}px) {
    align-self: end;
  }
`

const StyledSearch = styled(Search)`
  margin: auto 0px auto -35px;
  font-size: 20;
  cursor: 'pointer';
`

export default OrgGrid
